import { css } from '@inter-ikea-kompis/web-component';
import { ColorConverter } from '@inter-ikea-kompis/utilities';
import IconButtonOverlayColorEnum from '../../enums/IconButtonOverlayColorEnum.js';
import IconButtonSizeEnum from '../../enums/IconButtonSizeEnum.js';
const BUTTON_HEIGHT = {
    xsmall: 24,
    small: 40,
    medium: 56
};
const CLICKABLE_AREA = {
    xsmallButton: 10,
    smallButton: 2
};
/**
 * IconButton Style.
 */
export default class IconButtonStyle {
    /**
     * Returns styles.
     *
     * @param options Options.
     * @returns Styles.
     */
    static getStyle(options) {
        return css `
			${this.getCommonStyle(options)}
			${this.getLayoutStyle(options)}
			${options.isDisabled ? this.getDisabled(options) : ''}
		`;
    }
    /**
     * Returns overlay color styles.
     *
     * @param options Options.
     * @param color ...
     * @returns Styles.
     */
    static getColor(options, color) {
        const { neutral } = options.theme.color;
        switch (color) {
            case IconButtonOverlayColorEnum.IMAGE_OVERLAY_DARKEST:
                return ColorConverter.multiplyOpacity(neutral.grey900, 0.7);
            case IconButtonOverlayColorEnum.IMAGE_OVERLAY_COLOR:
                return ColorConverter.multiplyOpacity(neutral.grey900, 0.6);
            case IconButtonOverlayColorEnum.IMAGE_OVERLAY_DARK:
                return ColorConverter.multiplyOpacity(neutral.grey900, 0.65);
            case IconButtonOverlayColorEnum.BUTTON_COLOR_DARK:
                return ColorConverter.multiplyOpacity(neutral.white, 0.15);
            case IconButtonOverlayColorEnum.BUTTON_COLOR_LIGHT:
                return ColorConverter.multiplyOpacity(neutral.white, 0.5);
            case IconButtonOverlayColorEnum.BUTTON_COLOR_LIGHT_DARK:
                return ColorConverter.multiplyOpacity(neutral.white, 0.3);
            case IconButtonOverlayColorEnum.BUTTON_COLOR_SECONDARY_INVERSE:
                return ColorConverter.multiplyOpacity(neutral.white, 0.1);
            case IconButtonOverlayColorEnum.BUTTON_COLOR_SECONDARY:
                return ColorConverter.multiplyOpacity(neutral.grey900, 0.1);
        }
    }
    /**
     * Returns styles.
     *
     * @param options Options.
     * @returns Styles.
     */
    static getCommonStyle(options) {
        const { motion, border } = options.theme;
        return css `
			:host {
				display: inline-block;
				${options.isDisabled ? 'pointer-events: none;' : 'pointer-events: auto;'}
			}

			.visuallyHidden {
				border: 0 !important;
				clip: rect(${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem, ${1 / 16}rem) !important;
				-webkit-clip-path: inset(50%) !important;
				clip-path: inset(50%) !important;
				height: ${1 / 16}rem !important;
				margin: -${1 / 16}rem !important;
				overflow: hidden !important;
				padding: 0 !important;
				position: absolute !important;
				width: ${1 / 16}rem !important;
				white-space: nowrap !important;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
			}

			button {
				position: relative;
				border-radius: 50%;
				pointer-events: initial;
				transition-property: background, transform;
				transition-duration: ${motion.duration.small};
				transition-timing-function: ${motion.ease.easy};
				height: ${this.getButtonSize(options)};
				width: ${this.getButtonSize(options)};
				box-sizing: border-box;
				cursor: pointer;
				display: flex;
				justify-content: center;
				overflow: visible;
				align-items: center;
				/* Override user agent styling */
				padding: 0;
				border: 0;
				outline: none;
				font-weight: bold;
			}

			button:active {
				transform: scale(0.98);
			}

			button::-moz-focus-inner {
				border: 0;
			}

			.icon {
				display: inline-flex;
				justify-content: center;
				transition-duration: ${options.isLoading ? motion.duration.xsmall : motion.duration.large};
				transition-timing-function: ${motion.ease.easy};
				opacity: ${options.isLoading ? 0 : 1};
			}

			${this.getTransform(options)}

			.loading-indicator-container {
				position: absolute;
				height: 100%;
				display: flex;
				align-items: center;
			}

			.icon kompis-icon {
				flex-shrink: 0;
				flex-grow: 0;
			}

			kompis-focus-outline {
				display: flex;
				border-radius: ${border.radius.radius64 / 16}rem;
			}

			${this.getFocusStyle(options)}

			${!options.isDisabled && options.isLoading ? this.getLoading() : ''}

			${this.getClickableArea(options)}
		`;
    }
    /**
     * Returns button size.
     *
     * @param options ...
     */
    static getButtonSize(options) {
        switch (options.size) {
            case IconButtonSizeEnum.xsmall:
                return `${BUTTON_HEIGHT.xsmall / 16}rem`;
            case IconButtonSizeEnum.small:
                return `${BUTTON_HEIGHT.small / 16}rem`;
            case IconButtonSizeEnum.medium:
                return `${BUTTON_HEIGHT.medium / 16}rem`;
            default:
                return `${BUTTON_HEIGHT.medium / 16}rem`;
        }
    }
    /**
     * Returns the style for focusing the button.
     *
     * @param options Options.
     * @returns Styles.
     */
    static getFocusStyle(options) {
        return css `
			button:focus {
				background: ${this.getFocusColor(options)};
			}
		`;
    }
    /**
     * Returns the text color when focusing.
     *
     * @param options Options.
     * @returns Styles.
     */
    static getFocusColor(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
                return options.invertedColors ? color.background.alt : color.component.primaryHover;
            case 'emphasised':
                return options.invertedColors ? color.background.alt : color.accent.primaryAccent;
            case 'secondary':
                return color.transparency.default;
            case 'tertiary':
                return options.invertedColors
                    ? IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_DARK)
                    : color.background.alt;
            case 'danger':
                return color.semantic.negative;
            case 'imageOverlay':
            case 'image-overlay':
                return IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.IMAGE_OVERLAY_COLOR);
            default:
                return color.component.primaryHover;
        }
    }
    /**
     * Provide buttonType specific styles.
     *
     * @param options Options.
     * @returns Css.
     */
    static getLayoutStyle(options) {
        switch (options.buttonType) {
            case 'primary':
                return this.getLayoutPrimary(options);
            case 'emphasised':
                return this.getLayoutEmphasised(options);
            case 'secondary':
                return this.getLayoutSecondary(options);
            case 'tertiary':
                return this.getLayoutTertiary(options);
            case 'danger':
                return this.getLayoutDanger(options);
            case 'imageOverlay':
            case 'image-overlay':
                return this.getLayoutImageOverlayButton(options);
        }
    }
    /**
     * @returns Loading style.
     */
    static getLoading() {
        return css `
			button {
				pointer-events: none;
			}

			.container {
				visibility: hidden;
			}
		`;
    }
    /**
     * Style for button disabled.
     *
     * @param options Options.
     * @returns Style for button disabled.
     */
    static getDisabled(options) {
        return css `
			button {
				color: ${this.getDisabledColor(options)};
				background: ${this.getDisabledBackground(options)};
				pointer-events: none;
			}
		`;
    }
    /**
     * Returns disabled text color.
     *
     * @param options Options.
     * @returns Style for button disabled.
     */
    static getDisabledColor(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
            case 'emphasised':
            case 'secondary':
            case 'danger':
            case 'imageOverlay':
            case 'image-overlay':
                return color.neutral.grey500;
            case 'tertiary':
                return color.disabled.default;
        }
    }
    /**
     * Returns disabled background color.
     *
     * @param options Options.
     * @returns Style for button disabled.
     */
    static getDisabledBackground(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
            case 'emphasised':
            case 'secondary':
                return options.invertedColors
                    ? IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_DARK)
                    : color.disabled.default;
            case 'danger':
                return color.disabled.default;
            case 'imageOverlay':
            case 'image-overlay':
                return IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.IMAGE_OVERLAY_DARKEST);
            case 'tertiary':
                return 'transparent';
        }
    }
    /**
     * Returns background color for button types.
     *
     * @param options Options.
     */
    static getBackgroundColor(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
                return options.invertedColors ? color.background.default : color.neutral.grey900;
            case 'emphasised':
                return options.invertedColors ? color.background.default : color.accent.primaryAccent;
            case 'secondary':
                return color.transparency.default;
            case 'tertiary':
                return 'transparent';
            case 'danger':
                return color.semantic.negative;
            case 'imageOverlay':
            case 'image-overlay':
                return IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.IMAGE_OVERLAY_COLOR);
        }
    }
    /**
     * Returns hover color for button types.
     *
     * @param options Options.
     */
    static getHoverColor(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
                return options.invertedColors ? color.neutral.grey100 : color.component.primaryHover;
            case 'emphasised':
                return options.invertedColors ? color.neutral.grey100 : color.accent.primaryAccentDark;
            case 'secondary':
                return options.invertedColors
                    ? IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_DARK)
                    : color.border.dark;
            case 'tertiary':
                return options.invertedColors
                    ? IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_DARK)
                    : color.background.alt;
            case 'danger':
                return color.component.dangerHover;
            case 'imageOverlay':
            case 'image-overlay':
                return IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.IMAGE_OVERLAY_DARK);
        }
    }
    /**
     * Returns active color for button types.
     *
     * @param options Options.
     */
    static getActiveColor(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
                return options.invertedColors ? color.neutral.grey200 : color.component.primaryPress;
            case 'emphasised':
                return options.invertedColors ? color.neutral.grey200 : color.accent.primaryAccentDarkest;
            case 'secondary':
                return options.invertedColors ? color.border.inverse : color.border.dark;
            case 'tertiary':
                return options.invertedColors
                    ? IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_LIGHT_DARK)
                    : color.neutral.grey200;
            case 'danger':
                return color.component.dangerPress;
            case 'imageOverlay':
            case 'image-overlay':
                return IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.IMAGE_OVERLAY_DARKEST);
        }
    }
    /**
     * Returns text color for button types.
     *
     * @param options Options.
     */
    static getTextColor(options) {
        const { color } = options.theme;
        switch (options.buttonType) {
            case 'primary':
                return options.invertedColors ? color.text.dark : color.neutral.white;
            case 'emphasised':
                return options.invertedColors ? color.text.dark : color.neutral.white;
            case 'secondary':
                return options.invertedColors ? color.text.inverse : color.text.dark;
            case 'tertiary':
                return options.invertedColors ? color.text.inverse : color.text.dark;
            case 'danger':
                return color.neutral.white;
            case 'imageOverlay':
            case 'image-overlay':
                return color.text.inverse;
        }
    }
    /**
     * Style for primary buttonType.
     *
     * @param options Options.
     * @returns Style for primary buttonType.
     */
    static getLayoutPrimary(options) {
        return css `
			button {
				background: ${this.getBackgroundColor(options)};
				color: ${this.getTextColor(options)};
			}

			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				button:hover {
					background: ${this.getHoverColor(options)};
				}
			}

			button:active {
				background: ${this.getActiveColor(options)};
			}
		`;
    }
    /**
     * Style for text-only buttonType.
     *
     * @param options Options.
     * @returns Style for text-only buttonType.
     */
    static getLayoutEmphasised(options) {
        return css `
			button {
				background: ${this.getBackgroundColor(options)};
				color: ${this.getTextColor(options)};
			}

			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				button:hover {
					background: ${this.getHoverColor(options)};
				}
			}

			button:active {
				background: ${this.getActiveColor(options)};
			}
		`;
    }
    /**
     * Style for outlined buttonType.
     *
     * @param options Options.
     * @returns Style for outlined buttonType.
     */
    static getLayoutSecondary(options) {
        const { color } = options.theme;
        return css `
			button {
				background: ${this.getBackgroundColor(options)};
				border: ${1 / 16}rem solid
					${options.isDisabled
            ? 'none'
            : options.invertedColors
                ? color.border.inverse
                : color.border.dark};
				color: ${this.getTextColor(options)};
			}

			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				button:hover {
					border-color: ${this.getHoverColor(options)};
					box-shadow: inset 0 0 0 ${1 / 16}rem
						${options.isDisabled
            ? 'none'
            : options.invertedColors
                ? color.border.inverse
                : color.border.dark};
				}
			}

			button:focus {
				box-shadow: inset 0 0 0 ${1 / 16}rem
					${options.isDisabled
            ? 'none'
            : options.invertedColors
                ? color.border.inverse
                : color.border.dark};
			}

			button:active {
				border-color: ${this.getActiveColor(options)};
				background-color: ${options.invertedColors
            ? IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_SECONDARY_INVERSE)
            : IconButtonStyle.getColor(options, IconButtonOverlayColorEnum.BUTTON_COLOR_SECONDARY)};
			}
		`;
    }
    /**
     * @param options Options.
     * @returns Style for Tertiary buttonType.
     */
    static getLayoutTertiary(options) {
        return css `
			button {
				background: ${this.getBackgroundColor(options)};
				color: ${this.getTextColor(options)};
			}

			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				button:hover {
					background: ${this.getHoverColor(options)};
				}
			}

			button:active {
				background: ${this.getActiveColor(options)};
			}
		`;
    }
    /**
     * @param options Options.
     * @returns Style for danger buttonType.
     */
    static getLayoutDanger(options) {
        return css `
			button {
				background: ${this.getBackgroundColor(options)};
				color: ${this.getTextColor(options)};
			}

			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				button:hover {
					background: ${this.getHoverColor(options)};
				}
			}

			button:active {
				background: ${this.getActiveColor(options)};
			}
		`;
    }
    /**
     * Style for imageOverlay buttonType.
     *
     * @param options Options.
     * @returns Style for imageOverlay buttonType.
     */
    static getLayoutImageOverlayButton(options) {
        return css `
			button {
				background: ${this.getBackgroundColor(options)};
				color: ${this.getTextColor(options)};
			}

			/* Prevents hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				button:hover {
					background: ${this.getHoverColor(options)};
				}
			}

			button:active {
				background: ${this.getActiveColor(options)};
			}
		`;
    }
    /**
     * Transform for Icon button.
     *
     * @param options Options.
     */
    static getTransform(options) {
        if (options.isLoading) {
            return css `
				.icon {
					transform: translateY(30%);
				}
			`;
        }
        return '';
    }
    /**
     * Get the clickable area of button.
     *
     * @param options ...
     * @returns Style for the clicable area of button.
     */
    static getClickableArea(options) {
        const isButtonTypeSecondary = options.buttonType === 'secondary';
        switch (options.size) {
            case IconButtonSizeEnum.xsmall:
                return css `
					button:after {
						content: '';
						position: absolute;
						inset: -${isButtonTypeSecondary ? (CLICKABLE_AREA.xsmallButton + 1) / 16 : CLICKABLE_AREA.xsmallButton / 16}rem;
						display: block;
					}
				`;
            case IconButtonSizeEnum.small:
                return css `
					button:after {
						content: '';
						position: absolute;
						inset: -${isButtonTypeSecondary ? (CLICKABLE_AREA.smallButton + 1) / 16 : CLICKABLE_AREA.smallButton / 16}rem;
						display: block;
					}
				`;
            default:
                return css `
					button:after {
						content: none;
					}
				`;
        }
    }
}
//# sourceMappingURL=IconButtonStyle.js.map